import React, { useMemo, useState } from "react";

import useMedications from "../../features/medications/hooks/useMedications";
import MedicationList from "../../features/medications/components/MedicationList";
import MedicationDetailsDrawer from "../../features/medications/components/MedicationDetailsDrawer";

import EmptyState from "../../shared/ui/EmptyState";
import FeedbackBanner from "../../shared/ui/FeedbackBanner";
import SimplifiedMedicationModal from "../../features/medications/components/SimplifiedMedicationModal";
import { useStockPage } from "../../features/stock/hooks/useStockPage";

const STATUS_ORDER = {
  acabou: 0,
  acabando: 1,
  disponivel: 2,
  inativo: 3,
};

const FILTER_OPTIONS = [
  { value: "todos", label: "Todas" },
  { value: "acabou", label: "Sem estoque" },
  { value: "acabando", label: "Atenção" },
  { value: "disponivel", label: "Disponíveis" },
  { value: "inativo", label: "Inativas" },
];

export default function Medications() {
  const { items, loading, error, refetch } = useMedications();
  const [isSimplifiedModalOpen, setIsSimplifiedModalOpen] = useState(false);
  const [saveSuccessToast, setSaveSuccessToast] = useState("");

  const {
    startEdit,
    updateStatus,
    deleteItem,
  } = useStockPage();

  const [filter, setFilter] = useState("todos");
  const [selectedMedication, setSelectedMedication] = useState(null);
  const [initialActiveIngredient, setInitialActiveIngredient] = useState("");
  const [editingPackage, setEditingPackage] = useState(null);
  const [templatePackage, setTemplatePackage] = useState(null);

  const showSuccessToast = (msg) => {
    setSaveSuccessToast(msg);
    setTimeout(() => {
      setSaveSuccessToast("");
    }, 4000);
  };

  // Mantém o drawer sincronizado quando o estoque recarrega
  React.useEffect(() => {
    if (selectedMedication) {
      const updated = items.find((item) => item.id === selectedMedication.id);
      if (updated) {
        setSelectedMedication(updated);
      }
    }
  }, [items]);

  const activeCount = useMemo(() => {
    return items.filter(
      (item) =>
        item.status === "acabou" ||
        item.status === "acabando" ||
        item.status === "disponivel"
    ).length;
  }, [items]);

  const attentionCount = useMemo(() => {
    return items.filter(
      (item) => item.status === "acabou" || item.status === "acabando"
    ).length;
  }, [items]);

  const processedItems = useMemo(() => {
    let filtered = items;

    if (filter === "acabando") {
      // Aba "Atenção": exibe medicações que estão acabando ou já sem estoque
      filtered = items.filter(
        (item) => item.status === "acabando" || item.status === "acabou"
      );
    } else if (filter !== "todos") {
      filtered = items.filter((item) => item.status === filter);
    }

    return [...filtered].sort((a, b) => {
      return STATUS_ORDER[a.status] - STATUS_ORDER[b.status];
    });
  }, [items, filter]);

  if (loading) {
    return (
      <div className="space-y-4 py-2 animate-pulse">
        <div className="h-28 rounded-3xl skeleton-loading w-full" />
        <div className="h-12 rounded-2xl skeleton-loading w-full" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <div className="h-40 rounded-3xl skeleton-loading w-full" />
          <div className="h-40 rounded-3xl skeleton-loading w-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-3">
        <FeedbackBanner variant="error">{error}</FeedbackBanner>
        <div className="liti-card text-center p-6">
          <EmptyState
            title="Não conseguimos carregar suas medicações"
            description="Tente novamente em alguns instantes."
            actionLabel="Tentar novamente"
            onAction={refetch}
          />
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {/* BALÃO DE CONFIRMAÇÃO DE SALVAMENTO DE DADOS */}
        {saveSuccessToast && (
          <div className="rounded-2xl border border-[#CBF1DB] bg-[#CBF1DB] p-4 text-center text-xs font-bold text-emerald-900 shadow-md animate-in fade-in zoom-in-95 duration-200 flex items-center justify-center gap-2">
            <span className="text-lg">✨</span>
            <span>{saveSuccessToast}</span>
          </div>
        )}

        {/* CARD HERO DE RESUMO LITI */}
        <div className="liti-card p-5 space-y-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-[#24152F] font-serif">
                Estoque por Princípio Ativo
              </h2>
              <p className="text-xs text-[#6D6680] font-medium mt-0.5">
                Cadastre caixas e dosagens vinculadas ao Princípio Ativo com débito automático.
              </p>
            </div>

            <button
              type="button"
              onClick={() => {
                setEditingPackage(null);
                setTemplatePackage(null);
                setInitialActiveIngredient("");
                setIsSimplifiedModalOpen(true);
              }}
              className="btn-primary text-xs font-bold h-11 px-5 shrink-0 flex items-center justify-center gap-1.5 cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Cadastrar Caixa / Embalagem
            </button>
          </div>

          {/* INDICADORES EM PILLS LITI */}
          <div className="grid grid-cols-2 gap-3 pt-1">
            <div className="flex items-center justify-between rounded-2xl bg-[#ECE7FA] px-4 py-3 border border-[#ECE7FA]">
              <span className="text-xs font-bold text-[#6C54D8]">Em Uso</span>
              <span className="text-lg font-black text-[#24152F] font-serif">{activeCount}</span>
            </div>

            <div className={`flex items-center justify-between rounded-2xl px-4 py-3 border ${attentionCount > 0 ? "bg-amber-50 border-amber-200 text-amber-900" : "bg-[#ECE7FA] border-[#ECE7FA] text-[#6C54D8]"}`}>
              <span className="text-xs font-bold">Atenção</span>
              <span className={`text-lg font-black font-serif ${attentionCount > 0 ? "text-amber-600" : "text-[#24152F]"}`}>
                {attentionCount}
              </span>
            </div>
          </div>
        </div>

        {/* BARRA DE FILTROS (PILL TABS LITI) */}
        <div className="liti-card p-2 flex items-center gap-1.5 overflow-x-auto scrollbar-none shadow-xs">
          {FILTER_OPTIONS.map((option) => {
            const isActive = filter === option.value;
            return (
              <button
                key={option.value}
                onClick={() => setFilter(option.value)}
                className={`px-4 py-2 rounded-full text-xs font-bold transition-all duration-200 shrink-0 cursor-pointer ${
                  isActive
                    ? "bg-[#6C54D8] text-white shadow-xs"
                    : "text-[#6D6680] hover:text-[#6C54D8] hover:bg-[#ECE7FA]/60"
                }`}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {/* LISTA DE MEDICAÇÕES */}
        <MedicationList
          medications={processedItems}
          onViewDetail={(medication) => {
            setSelectedMedication(medication);
          }}
        />
      </div>

      {/* MODAL SIMPLIFICADO DE EMBALAGEM / PRINCIPIO ATIVO */}
      <SimplifiedMedicationModal
        isOpen={isSimplifiedModalOpen}
        initialActiveIngredient={initialActiveIngredient}
        editingItem={editingPackage}
        templateItem={templatePackage}
        onClose={() => {
          setIsSimplifiedModalOpen(false);
          setInitialActiveIngredient("");
          setEditingPackage(null);
          setTemplatePackage(null);
        }}
        onSuccess={async () => {
          setIsSimplifiedModalOpen(false);
          setInitialActiveIngredient("");
          setEditingPackage(null);
          setTemplatePackage(null);
          await refetch();
          showSuccessToast("Dados da embalagem salvos no estoque com sucesso!");
        }}
      />

      {/* GAVETA DE DETALHES */}
      <MedicationDetailsDrawer
        medication={selectedMedication}
        isOpen={Boolean(selectedMedication)}
        onClose={() => setSelectedMedication(null)}
        onAddPackage={(med) => {
          setSelectedMedication(null);
          setEditingPackage(null);
          setTemplatePackage(med);
          setInitialActiveIngredient(med?.name || "");
          setIsSimplifiedModalOpen(true);
        }}
        onEditPackage={(item) => {
          setSelectedMedication(null);
          setEditingPackage(item);
          setTemplatePackage(null);
          setIsSimplifiedModalOpen(true);
        }}
        onUpdatePackageStatus={async (id, action) => {
          await updateStatus(id, action);
          await refetch();
          showSuccessToast("Status da embalagem atualizado com sucesso!");
        }}
        onDeletePackage={async (id) => {
          await deleteItem(id);
          await refetch();
          showSuccessToast("Embalagem removida do estoque com sucesso!");
        }}
      />
    </>
  );
}